/**
 * Calls from numbers the CRM does not know. The phone system files every call; one that matches
 * nobody is kept here, rather than inventing a contact named after a phone number, until someone
 * says who it was. Linking one links every other call from that number too.
 */
import { dispositionLabel, formatPhone } from "@tlcrm/shared";
import { useState } from "react";

import { errorMessage } from "../api/client";
import { useLinkCall, useSearch, useUnmatchedCalls, type UnmatchedCall } from "../api/hooks";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toasts";
import { EmptyState, ErrorState, Modal } from "../components/ui";
import { NewContactModal } from "../forms/NewContact";
import { duration, when } from "../lib/format";

function LinkModal({ call, onClose }: { call: UnmatchedCall; onClose: () => void }) {
  const [q, setQ] = useState("");
  const search = useSearch(q);
  const link = useLinkCall();
  const toast = useToast();
  const choose = async (contactId: string, name: string) => {
    try {
      const r = await link.mutateAsync({ callId: call.id, contactId });
      toast(`Linked ${r.linked} ${r.linked === 1 ? "call" : "calls"} to ${name}.`);
      onClose();
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };
  return (
    <Modal title={`Who was ${call.phone ? formatPhone(call.phone) : "this"}?`} onClose={onClose}>
      <div className="stack">
        <input
          className="field"
          placeholder="Search by name or another number"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        <div className="card">
          <div className="rel">
            {q.trim().length < 2 && (
              <div className="rel-empty">Type a name to find the person.</div>
            )}
            {search.data?.contacts.map((c) => (
              <button
                key={c.id}
                className="rel-item"
                style={{ textAlign: "left", width: "100%" }}
                onClick={() => void choose(c.id, c.fullName)}
              >
                <div className="t">{c.fullName}</div>
                <div className="s">
                  {c.primaryPhone && <span className="mono">{formatPhone(c.primaryPhone)}</span>}
                  {c.state && <span>{c.state}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export function UnmatchedCalls() {
  const calls = useUnmatchedCalls();
  const [linking, setLinking] = useState<UnmatchedCall | null>(null);
  const [creating, setCreating] = useState(false);
  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <div className="page-head">
        <div>
          <h1>Unmatched calls</h1>
          <p>
            Calls from numbers on nobody's record. Say who it was, and the call joins their
            timeline.
          </p>
        </div>
      </div>
      <div className="table-wrap">
        {calls.isError ? (
          <ErrorState error={calls.error} onRetry={() => void calls.refetch()} />
        ) : calls.data?.length === 0 ? (
          <EmptyState icon="phoneIn" title="Every call is matched">
            New numbers show up here after the desk files their call.
          </EmptyState>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>When</th>
                <th>Number</th>
                <th>Direction</th>
                <th>Line</th>
                <th>Outcome</th>
                <th>Agent</th>
                <th className="num">Length</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {calls.data?.map((c) => (
                <tr key={c.id} style={{ cursor: "default" }}>
                  <td>{when(c.startedAt)}</td>
                  <td className="mono primary">{c.phone ? formatPhone(c.phone) : "Withheld"}</td>
                  <td>
                    <span
                      style={{ display: "inline-flex", gap: "var(--s-3)", alignItems: "center" }}
                    >
                      <Icon
                        name={c.direction === "inbound" ? "phoneIn" : "phoneOut"}
                        className="t3"
                      />
                      {c.direction === "inbound" ? "Inbound" : "Outbound"}
                    </span>
                  </td>
                  <td>{c.line ?? c.queue ?? <span className="t3">–</span>}</td>
                  <td title={c.summary ?? undefined}>
                    {c.disposition ? (
                      dispositionLabel(c.disposition)
                    ) : (
                      <span className="t3">Not wrapped</span>
                    )}
                  </td>
                  <td>{c.agent?.fullName ?? <span className="t3">–</span>}</td>
                  <td className="num mono">{duration(c.durationSeconds)}</td>
                  <td className="num">
                    <button className="btn btn-sm" onClick={() => setLinking(c)}>
                      <Icon name="link" /> Link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="t3" style={{ fontSize: "var(--fs-xs)", marginTop: "var(--s-5)" }}>
        Somebody new?{" "}
        <button className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>
          Add them as a contact
        </button>{" "}
        with the number, and their calls match on their own from then on.
      </p>
      {linking && <LinkModal call={linking} onClose={() => setLinking(null)} />}
      {creating && <NewContactModal onClose={() => setCreating(false)} />}
    </div>
  );
}
